import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleHelp, Loader2, PackageCheck, Truck, Wrench } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { CrewPickerSelect } from '../job/JobFormRows';
import { CREW_KINDS, crewKind } from '../../lib/crewKinds';
import { cn } from '../../lib/utils';
import ShotNumber from './ShotNumber';

function ItemRow({ item, crewMembers, onAssign, busy }) {
  const hire = item.kind === 'hire';
  const pushable = !item.on_job && (!hire || item.supplier_id);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3 border-t border-line first:border-t-0">
      <span className="flex-[1_1_200px]">
        {item.name}
        {item.quantity > 1 && <span className="text-muted-foreground font-normal"> ×{item.quantity}</span>}
      </span>
      <span className="flex flex-wrap gap-1" aria-label="Used in shots">
        {item.shots.map((n) => (
          <ShotNumber key={n} number={n} />
        ))}
      </span>
      <span className={cn('w-24 text-sm flex items-center gap-1.5', item.on_job ? 'text-moss' : pushable ? 'text-foreground' : 'text-muted-foreground')}>
        <span aria-hidden="true" className={cn('w-1.5 h-1.5 rounded-full', item.on_job ? 'bg-moss' : pushable ? 'bg-foreground' : 'bg-ink-faded')} />
        {item.on_job ? 'On job' : pushable ? 'Will add' : 'Waiting'}
      </span>
      {hire && (
        <div className="w-full sm:w-56">
          <CrewPickerSelect
            crewMembers={crewMembers}
            onSelect={(crewId) => onAssign(item, crewId)}
            triggerLabel={busy ? 'Saving…' : item.supplier_id ? 'Change supplier' : 'Choose supplier'}
            emptyLabel="Add suppliers on Crew / Suppliers"
            isSuggested={(crew) => item.kit_supplier_ids.includes(crew.id)}
            suggestedLabel="Has it in their kit list"
          />
        </div>
      )}
    </div>
  );
}

/**
 * The Sourcing tab: what the treatment needs, grouped by where it comes from,
 * and the one-way push to the linked job. The roll-up is computed by the
 * backend (treatment_service.roll_up) from the saved treatment, so this tab
 * asks for a save first when there are unsaved edits.
 */
export default function TreatmentSourcing({ treatmentId, dirty, onSave, onAssign, linkedJobId }) {
  const { getTreatmentSourcing, pushTreatmentToJob, crewMembers, fetchCrewMembers } = useAppStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [billDirect, setBillDirect] = useState(false);
  const [divider, setDivider] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getTreatmentSourcing(treatmentId));
    } catch (e) {
      toast.error('Could not load sourcing');
    } finally {
      setLoading(false);
    }
  }, [getTreatmentSourcing, treatmentId]);

  useEffect(() => {
    fetchCrewMembers();
  }, [fetchCrewMembers]);

  useEffect(() => {
    if (!dirty) refresh();
  }, [dirty, refresh, linkedJobId]);

  if (dirty && !busy) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <p className="text-muted-foreground">Sourcing works from the saved shot list.</p>
          <Button onClick={onSave}>Save to see sourcing</Button>
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <div className="flex justify-center py-16">
        {loading && <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  // Assigning saves straight away: this tab only shows a saved treatment.
  const assign = async (item, crewId) => {
    setBusy(true);
    const ok = await onAssign(item.equipment_ids, crewId);
    setBusy(false);
    if (ok) refresh();
  };

  const crewById = Object.fromEntries(crewMembers.map((c) => [c.id, c]));
  const inventory = data.items.filter((i) => i.kind === 'inventory');
  const unassigned = data.items.filter((i) => i.kind === 'hire' && !i.supplier_id);
  const bySupplier = {};
  data.items
    .filter((i) => i.kind === 'hire' && i.supplier_id)
    .forEach((i) => {
      (bySupplier[i.supplier_id] = bySupplier[i.supplier_id] || []).push(i);
    });

  const toAdd = data.items.filter((i) => !i.on_job && (i.kind === 'inventory' || i.supplier_id));
  const hasSupplierLines = toAdd.some((i) => i.kind === 'hire' && crewKind(crewById[i.supplier_id]) === 'supplier');

  const push = async () => {
    const job = data.job;
    if (!window.confirm(`Add ${toAdd.length} item${toAdd.length === 1 ? '' : 's'} to ${job.job_number || job.project_name}? Nothing already on the job changes.`)) return;
    setBusy(true);
    try {
      const result = await pushTreatmentToJob(treatmentId, { suppliers_bill_direct: billDirect, divider });
      const added = result.added_equipment + result.added_crew_equipment;
      toast.success(`Added ${added} item${added === 1 ? '' : 's'} to the job`);
      result.skipped.forEach((s) => toast(`${s.name} left out: ${s.reason}`));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to the job');
    } finally {
      setBusy(false);
    }
  };

  const group = (key, icon, title, tag, items, tone) => (
    <Card key={key} className={cn(tone === 'warn' && 'bg-ochre-soft')}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="section-title flex items-center gap-2">
          {icon} {title}
        </CardTitle>
        {tag && <Badge variant="secondary" className="text-xs">{tag}</Badge>}
      </CardHeader>
      <CardContent className="p-0 pb-2">
        {items.map((item) => (
          <ItemRow key={item.key} item={item} crewMembers={crewMembers} onAssign={assign} busy={busy} />
        ))}
      </CardContent>
    </Card>
  );

  if (!data.items.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No equipment on any shot yet. Add it on the Shots tab.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-8 min-w-0 space-y-6">
        {inventory.length > 0 && group('inv', <Wrench className="w-[18px] h-[18px]" />, 'Our inventory', null, inventory)}
        {Object.entries(bySupplier).map(([id, items]) => {
          const crew = crewById[id];
          return group(
            id,
            <Truck className="w-[18px] h-[18px]" />,
            crew?.name || 'Unknown supplier',
            crew ? CREW_KINDS[crewKind(crew)].label : 'Archived or deleted',
            items,
          );
        })}
        {unassigned.length > 0 && group('none', <CircleHelp className="w-[18px] h-[18px] text-ochre" />, 'Needs a supplier', null, unassigned, 'warn')}
      </div>

      <Card className="lg:col-span-4">
        <CardHeader>
          <CardTitle className="section-title flex items-center gap-2"><PackageCheck className="w-[18px] h-[18px]" /> Add to job</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!data.job ? (
            <p className="text-sm text-muted-foreground">Link a job above to add this equipment to it.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Adds what <Link className="text-clay underline" to={`/jobs/${data.job.id}`}>{data.job.job_number || data.job.project_name}</Link> doesn't
                have yet. Nothing already on the job changes.
              </p>
              {unassigned.length > 0 && (
                <p className="text-sm text-ochre">
                  {unassigned.length} hire{unassigned.length === 1 ? ' needs' : 's need'} a supplier first.
                </p>
              )}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="push-divider" checked={divider} onCheckedChange={(v) => setDivider(!!v)} />
                  <Label htmlFor="push-divider" className="font-normal">Group under a "From shot list" divider</Label>
                </div>
                {hasSupplierLines && (
                  <div className="flex items-center gap-2">
                    <Checkbox id="push-billdirect" checked={billDirect} onCheckedChange={(v) => setBillDirect(!!v)} />
                    <Label htmlFor="push-billdirect" className="font-normal">Suppliers bill the client directly</Label>
                  </div>
                )}
              </div>
              <Button className="w-full" onClick={push} disabled={busy || toAdd.length === 0} data-testid="push-to-job-btn">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {toAdd.length ? `Add ${toAdd.length} item${toAdd.length === 1 ? '' : 's'} to job` : 'Everything is on the job'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
